<script>
import { defineComponent, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const SEGMENT_2_THRESHOLD = 80;
const SEGMENT_3_THRESHOLD = 90;

export default defineComponent({
  name: 'LimitProgressBar',
  emits: ['progress-percentage-changed'],
  props: {
    // Optional test-id override for unique identification
    testId: {
      type: String,
      required: false,
      default: 'unit-limiter-progress-bar'
    },
    // The limit value based on the config and baseDate
    limitValue: {
      type: Number,
      required: false,
      default: 0
    },
    // The count of appointments against the limit
    count: {
      type: Number,
      required: true
    },
    // The date for which the limit is being evaluated,
    // Only used here for watching changes
    baseDate: {
      type: [Date, String],
      required: false
    },
    // The mode for date evaluation: 'day' or 'month'
    // Only used here for watching changes
    dateMode: {
      type: String,
      required: false
    },
    // Optional title to display (e.g., "Appointments" or "Units")
    title: {
      type: String,
      required: false,
      default: ''
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    /*
     * Compute the current limit based on the config and baseDate props
     * The config contains limits for days of the week and months
     * The baseDate is used to determine which limit to use based on the dateMode
     * For 'day' period, use the day of the week (e.g., monday, tuesday, etc.)
     * For 'month' period, use the month name (e.g., january, february, etc.)
     */

    const progressPercentage = computed(() => {
      if (!props.limitValue || props.limitValue === 0) {
        return 0;
      }
      return Math.max(0, Math.min((props.count / props.limitValue) * 100, 100));
    });

    const actualPercentage = computed(() => {
      if (!props.limitValue || props.limitValue === 0) {
        return 0;
      }
      return Math.max(0, (props.count / props.limitValue) * 100);
    });

    const segment2PercentageColor = computed(() => {
      return progressPercentage.value >= SEGMENT_2_THRESHOLD
        ? 'text--color-text-primary'
        : 'text--color-text-disabled';
    });

    const segment3PercentageColor = computed(() => {
      return progressPercentage.value >= SEGMENT_3_THRESHOLD
        ? 'text--color-text-primary'
        : 'text--color-text-disabled';
    });

    const getSegmentWidth = segment => {
      const percentage = progressPercentage.value;

      switch (segment) {
        case 1:
          return Math.min(percentage, SEGMENT_2_THRESHOLD);
        case 2:
          return percentage > SEGMENT_2_THRESHOLD
            ? Math.min(percentage - SEGMENT_2_THRESHOLD, SEGMENT_3_THRESHOLD - SEGMENT_2_THRESHOLD)
            : 0;
        case 3:
          return percentage > SEGMENT_3_THRESHOLD ? percentage - SEGMENT_3_THRESHOLD : 0;
        default:
          return 0;
      }
    };

    watch(
      () => [progressPercentage.value, props.baseDate, props.dateMode, props.count],
      () => {
        emit('progress-percentage-changed', { progressPercentage: actualPercentage.value ?? 0 });
      },
      { immediate: true }
    );

    return {
      t,
      progressPercentage,
      actualPercentage,
      segment2PercentageColor,
      segment3PercentageColor,
      getSegmentWidth,
      SEGMENT_2_THRESHOLD,
      SEGMENT_3_THRESHOLD
    };
  }
});
</script>
<template>
  <div class="d-flex flex-column gap-2" :data-testid="testId">
    <div class="d-flex flex-row align-center">
      <span
        v-if="title"
        class="typography-body-sm font-weight-bold text--color-text-secondary"
        data-testid="unit-limiter-progress-title">
        {{ title }}
      </span>
      <v-spacer />
      <span data-testid="unit-limiter-progress-value">
        <strong>{{ count }}</strong>
        / {{ limitValue }}
        <span class="typography-body-sm ml-2" data-testid="unit-limiter-progress-percentage">
          ({{ actualPercentage ? Math.round(actualPercentage) : 0 }}%)
        </span>
      </span>
    </div>

    <div class="progress-wrapper">
      <div class="progress-container">
        <div class="progress-background">
          <div
            class="progress-segment segment-1 bg-success"
            :style="{ width: getSegmentWidth(1) + '%' }"></div>

          <div
            class="progress-segment segment-2"
            :class="getSegmentWidth(2) > 0 ? 'bg-yellow' : ''"
            :style="{ width: getSegmentWidth(2) + '%' }"></div>

          <div
            class="progress-segment segment-3"
            :class="getSegmentWidth(3) > 0 ? 'bg-red' : ''"
            :style="{ width: getSegmentWidth(3) + '%' }"></div>
        </div>

        <div
          class="segment-divider-dashed segment-divider-80"
          :class="segment2PercentageColor"></div>
        <div
          class="segment-divider-dashed segment-divider-90"
          :class="segment3PercentageColor"></div>
      </div>

      <div class="percentage-labels">
        <span class="percentage-label percentage-label-80" :class="segment2PercentageColor">
          {{ SEGMENT_2_THRESHOLD }}%
        </span>
        <span class="percentage-label percentage-label-90" :class="segment3PercentageColor">
          {{ SEGMENT_3_THRESHOLD }}%
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.progress-wrapper {
  position: relative;
  width: 100%;
}

.progress-container {
  position: relative;
  width: 100%;
  height: 20px;
}

.progress-background {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
}

.progress-segment {
  height: 100%;
  transition: width 0.3s ease;
  position: relative;
}

.segment-1 {
  max-width: 80%;
}

.segment-2 {
  max-width: 10%;
}

.segment-3 {
  max-width: 10%;
}

.segment-divider-dashed {
  position: absolute;
  top: 0px;
  height: 20px;
  width: 0;
  border-left: 2px dashed;
  z-index: 10;
  transform: translateX(-1px);
}

.segment-divider-80 {
  left: 80%;
}

.segment-divider-90 {
  left: 90%;
}

.percentage-labels {
  position: relative;
  width: 100%;
  height: 20px;
  margin-top: 2px;
}

.percentage-label {
  position: absolute;
  transform: translateX(-30%);
  font-size: 12px;
  top: 0;
}

.percentage-label-80 {
  left: 80%;
}

.percentage-label-90 {
  left: 90%;
}
</style>
